import { supabase } from '../lib/supabase';
import { Member, MemberFormData } from '../types/database';

export class MemberService {
  // Get members for a specific meeting type
  static async getMembersForMeetingType(meetingTypeId: string): Promise<{ data: Member[] | null; error: Error | null }> {
    try {
      const { data, error } = await supabase
        .from('members')
        .select(`
          *,
          meeting_types (
            id,
            name,
            level
          )
        `)
        .eq('meeting_type_id', meetingTypeId)
        .order('name');

      if (error) throw error;

      return { data: data as Member[], error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  }

  // Add new member
  static async addMember(formData: MemberFormData): Promise<{ data: Member | null; error: Error | null }> {
    try {
      const { data, error } = await supabase
        .from('members')
        .insert({
          name: formData.name,
          phone: formData.phone,
          meeting_type_id: formData.meeting_type_id,
          tags: formData.tags || null
        })
        .select(`
          *,
          meeting_types (
            id,
            name,
            level
          )
        `)
        .single();

      if (error) throw error;

      return { data: data as Member, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  }

  // Update member
  static async updateMember(memberId: string, formData: Partial<MemberFormData>): Promise<{ data: Member | null; error: Error | null }> {
    try {
      const { data, error } = await supabase
        .from('members')
        .update({
          name: formData.name,
          phone: formData.phone,
          meeting_type_id: formData.meeting_type_id,
          tags: formData.tags
        })
        .eq('id', memberId)
        .select(`
          *,
          meeting_types (
            id,
            name,
            level
          )
        `)
        .single();

      if (error) throw error;

      return { data: data as Member, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  }

  // Delete member
  static async deleteMember(memberId: string): Promise<{ error: Error | null }> {
    try {
      const { error } = await supabase
        .from('members')
        .delete()
        .eq('id', memberId);

      if (error) throw error;

      return { error: null };
    } catch (error) {
      return { error: error as Error };
    }
  }

  // Search members
  static async searchMembers(query: string, meetingTypeId?: string): Promise<{ data: Member[] | null; error: Error | null }> {
    try {
      let supabaseQuery = supabase
        .from('members')
        .select(`
          *,
          meeting_types (
            id,
            name,
            level
          )
        `);

      if (meetingTypeId) {
        supabaseQuery = supabaseQuery.eq('meeting_type_id', meetingTypeId);
      }

      const { data, error } = await supabaseQuery
        .or(`name.ilike.%${query}%,phone.ilike.%${query}%`)
        .order('name');

      if (error) throw error;

      return { data: data as Member[], error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  }

  // Get members with attendance summary for a convener's assigned meeting types
  static async getConvenorMemberAttendanceSummary(
    userId: string,
    meetingTypeId?: string,
    filters?: { startDate?: string; endDate?: string }
  ): Promise<{ data: any[] | null; error: Error | null }> {
    try {
      // 1. Get assigned meeting types for convener
      const { data: assignments } = await supabase
        .from('user_meeting_assignments')
        .select('meeting_type_id')
        .eq('user_id', userId);

      const assignedTypeIds = assignments?.map(a => a.meeting_type_id) || [];
      if (assignedTypeIds.length === 0) {
        return { data: [], error: null };
      }

      // Filter by meetingTypeId if provided, else all assigned
      const targetTypeIds = meetingTypeId ? [meetingTypeId] : assignedTypeIds;

      // 2. Get members for these meeting types
      const { data: members, error: membersError } = await supabase
        .from('members')
        .select(`
          *,
          meeting_types (
            id,
            name,
            level
          )
        `)
        .in('meeting_type_id', targetTypeIds)
        .order('name');

      if (membersError) throw membersError;
      if (!members || members.length === 0) return { data: [], error: null };

      // 3. Get meetings held by this convener for these meeting types with date filter
      let meetingQuery = supabase
        .from('meeting_instances')
        .select('id, date, meeting_type_id, status')
        .eq('created_by', userId)
        .in('meeting_type_id', targetTypeIds);

      if (filters?.startDate) {
        meetingQuery = meetingQuery.gte('date', filters.startDate);
      }
      if (filters?.endDate) {
        meetingQuery = meetingQuery.lte('date', filters.endDate);
      }

      const { data: meetings } = await meetingQuery;

      const meetingIds = meetings?.map(m => m.id) || [];
      const meetingDatesMap: Record<string, string> = {};
      meetings?.forEach(m => {
        meetingDatesMap[m.id] = m.date;
      });

      // 4. Get attendance records for these meetings
      let attendanceRecords: any[] = [];
      if (meetingIds.length > 0) {
        const { data: records } = await supabase
          .from('attendance_records')
          .select(`
            member_id,
            status,
            meeting_instance_id,
            meeting_instances (
              date
            )
          `)
          .in('meeting_instance_id', meetingIds);
        attendanceRecords = records || [];
      }

      // Map meetings by meeting_type_id to count meetings held per type
      const meetingsCountByType: Record<string, number> = {};
      meetings?.forEach(m => {
        meetingsCountByType[m.meeting_type_id] = (meetingsCountByType[m.meeting_type_id] || 0) + 1;
      });

      // Map attendance records by member_id
      const attendanceByMember: Record<string, {
        present: number;
        absent: number;
        total: number;
        presentDates: string[];
        absentDates: string[];
      }> = {};

      attendanceRecords.forEach(r => {
        if (!attendanceByMember[r.member_id]) {
          attendanceByMember[r.member_id] = {
            present: 0,
            absent: 0,
            total: 0,
            presentDates: [],
            absentDates: []
          };
        }
        const recordDate = r.meeting_instances?.date || meetingDatesMap[r.meeting_instance_id] || '';
        attendanceByMember[r.member_id].total++;
        if (r.status === 'Present') {
          attendanceByMember[r.member_id].present++;
          if (recordDate && !attendanceByMember[r.member_id].presentDates.includes(recordDate)) {
            attendanceByMember[r.member_id].presentDates.push(recordDate);
          }
        } else {
          attendanceByMember[r.member_id].absent++;
          if (recordDate && !attendanceByMember[r.member_id].absentDates.includes(recordDate)) {
            attendanceByMember[r.member_id].absentDates.push(recordDate);
          }
        }
      });

      // Sort dates
      Object.values(attendanceByMember).forEach(acc => {
        acc.presentDates.sort();
        acc.absentDates.sort();
      });

      // Combine member details with attendance stats
      const result = members.map(member => {
        const typeMeetingsCount = meetingsCountByType[member.meeting_type_id] || 0;
        const memberStats = attendanceByMember[member.id] || {
          present: 0,
          absent: 0,
          total: 0,
          presentDates: [],
          absentDates: []
        };
        const totalMeetingsForMember = typeMeetingsCount;
        const present = memberStats.present;
        const absent = memberStats.absent;
        const percentage = totalMeetingsForMember > 0 ? (present / totalMeetingsForMember) * 100 : 0;

        return {
          ...member,
          stats: {
            totalMeetings: totalMeetingsForMember,
            present,
            absent,
            percentage,
            presentDates: memberStats.presentDates,
            absentDates: memberStats.absentDates
          }
        };
      });

      return { data: result, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  }

  // Find existing members by phone number (for auto-lookup)
  static async findMemberByPhone(phone: string): Promise<{ data: Member[] | null; error: Error | null }> {
    try {
      const cleanPhone = phone.trim().replace(/[^0-9]/g, '');
      if (cleanPhone.length < 4) return { data: [], error: null };

      const { data, error } = await supabase
        .from('members')
        .select(`
          *,
          meeting_types (
            id,
            name,
            level
          )
        `)
        .ilike('phone', `%${cleanPhone}%`)
        .order('name')
        .limit(10);

      if (error) throw error;
      return { data: data as Member[], error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  }

  // Get members belonging to other meeting types assigned to convener
  static async getConvenerOtherMembers(
    userId: string,
    currentMeetingTypeId?: string
  ): Promise<{ data: Member[] | null; error: Error | null }> {
    try {
      const { data: assignments } = await supabase
        .from('user_meeting_assignments')
        .select('meeting_type_id')
        .eq('user_id', userId);

      const assignedTypeIds = assignments?.map(a => a.meeting_type_id) || [];
      if (assignedTypeIds.length === 0) return { data: [], error: null };

      const otherTypeIds = currentMeetingTypeId
        ? assignedTypeIds.filter(id => id !== currentMeetingTypeId)
        : assignedTypeIds;

      const typeIdsToQuery = otherTypeIds.length > 0 ? otherTypeIds : assignedTypeIds;

      const { data, error } = await supabase
        .from('members')
        .select(`
          *,
          meeting_types (
            id,
            name,
            level
          )
        `)
        .in('meeting_type_id', typeIdsToQuery)
        .order('name');

      if (error) throw error;
      return { data: data as Member[], error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  }

  // Get all system members for admin or global search
  static async getAllSystemMembers(): Promise<{ data: Member[] | null; error: Error | null }> {
    try {
      const { data, error } = await supabase
        .from('members')
        .select(`
          *,
          meeting_types (
            id,
            name,
            level
          )
        `)
        .order('name');

      if (error) throw error;
      return { data: data as Member[], error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  }

  // Bulk copy/insert members into a target meeting type
  static async bulkAddMembersToMeetingType(
    targetMeetingTypeId: string,
    members: { name: string; phone: string; tags?: string }[]
  ): Promise<{ data: Member[] | null; error: Error | null }> {
    try {
      if (members.length === 0) return { data: [], error: null };

      const recordsToInsert = members.map(m => ({
        name: m.name,
        phone: m.phone,
        meeting_type_id: targetMeetingTypeId,
        tags: m.tags || null
      }));

      const { data, error } = await supabase
        .from('members')
        .insert(recordsToInsert)
        .select(`
          *,
          meeting_types (
            id,
            name,
            level
          )
        `);

      if (error) throw error;
      return { data: data as Member[], error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  }
}
